"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FabricMapLocationSearch = exports.tryToSetPlaceholder = exports.coordinatesGeocoder = exports.coordinateFeature = void 0;
const react_1 = __importDefault(require("react"));
const mapbox_gl_geocoder_1 = __importDefault(require("@mapbox/mapbox-gl-geocoder"));
require("@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css");
const getApiKey_1 = require("../util/getApiKey");
const Context_1 = require("../map/Context");
const react_intl_1 = require("react-intl");
const FabricMapMarkers_1 = require("../layers/FabricMapMarkers");
const coordinateFeature = (lng, lat) => ({
    center: [lng, lat],
    geometry: {
        type: "Point",
        coordinates: [lng, lat],
    },
    place_name: `Lat: ${lat} Lng: ${lng}`,
    place_type: ["coordinate"],
    properties: {},
    type: "Feature",
}); // Not actually a result, but returns all the properties needed for geocoding
exports.coordinateFeature = coordinateFeature;
/**
 * Given a query in the form "lng, lat" or "lat, lng"
 * returns the matching geographic coordinate(s)
 * as search results in carmen geojson format:
 *
 * https://github.com/mapbox/carmen/blob/master/carmen-geojson.md
 *
 * This code comes from Mapbox themselves: https://docs.mapbox.com/mapbox-gl-js/example/mapbox-gl-geocoder-accept-coordinates/
 * */
const coordinatesGeocoder = (query) => {
    // Match anything which looks like
    // decimal degrees coordinate pair.
    const matches = query.match(/^[ ]*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)[ ]*$/i);
    if (!matches) {
        return [];
    }
    const coord1 = Number(matches[1]);
    const coord2 = Number(matches[2]);
    const geocodes = [];
    if (coord1 < -90 || coord1 > 90) {
        // must be lng, lat
        geocodes.push((0, exports.coordinateFeature)(coord1, coord2));
    }
    if (coord2 < -90 || coord2 > 90) {
        // must be lat, lng
        geocodes.push((0, exports.coordinateFeature)(coord2, coord1));
    }
    if (geocodes.length === 0) {
        // else could be either lng, lat or lat, lng
        geocodes.push((0, exports.coordinateFeature)(coord1, coord2));
        geocodes.push((0, exports.coordinateFeature)(coord2, coord1));
    }
    return geocodes;
};
exports.coordinatesGeocoder = coordinatesGeocoder;
const apiKey = (0, getApiKey_1.getApiKey)();
const geocoder = new mapbox_gl_geocoder_1.default({
    accessToken: apiKey,
    localGeocoder: exports.coordinatesGeocoder,
});
/**
 * The geocoder can be instantiated but internals aren't always updated to actually have
 * a placeholder to set. This is a very frustrating and intermittently timed error,
 * which this function prevents by just looping try-catch until we get a placeholder.
 */
const tryToSetPlaceholder = (message) => {
    try {
        geocoder.setPlaceholder(message);
    }
    catch (_a) {
        setTimeout(() => {
            (0, exports.tryToSetPlaceholder)(message);
        }, 100);
    }
};
exports.tryToSetPlaceholder = tryToSetPlaceholder;
/**
 * Adds a search widget to the upper-right corner of the map.
 * A successful search will drop a red pin on the map at the resolved location.
 */
exports.FabricMapLocationSearch = react_1.default.memo(() => {
    const intl = (0, react_intl_1.useIntl)();
    const map = (0, Context_1.useMap)();
    const [searchResultPosition, setSearchResultPosition] = react_1.default.useState(null);
    react_1.default.useEffect(() => {
        (0, exports.tryToSetPlaceholder)(intl.formatMessage({ id: "Search_for_a_location" }));
    }, [intl]);
    /**
     * When a user selects a search location, we drop a red pin on it.
     */
    const onResult = react_1.default.useCallback(({ result }) => {
        setSearchResultPosition(result.center);
    }, []);
    /**
     * Remove pins when user has cleared search.
     */
    const onClear = react_1.default.useCallback(() => {
        setSearchResultPosition(null);
    }, []);
    /**
     * Handle attaching and removing geocoder event handlers
     */
    react_1.default.useEffect(() => {
        /**
         * When the user selects an item in the search box, create a marker on the map
         */
        geocoder.on("result", onResult);
        /**
         * When the user clears search, remove the marker.
         */
        geocoder.on("clear", onClear);
        /**
         * Mapbox will not be smart and will attach these handlers every render cycle, so to
         * be cautious, we attach and remove whenever the component renders/de-renders (is that a word?).
         */
        return () => {
            geocoder.off("result", onResult);
            geocoder.off("clear", onClear);
        };
    }, [onClear, onResult]);
    /**
     * Attach geocoder to map and remove on dismount.
     */
    react_1.default.useEffect(() => {
        if (map && !map.hasControl(geocoder)) {
            map.addControl(geocoder);
        }
        else if (!map) {
            console.error("No map provider in FabricMapLocationSearch ancestry!");
        }
        return () => {
            if (map) {
                map.removeControl(geocoder);
            }
            setSearchResultPosition(null);
        };
    }, [map]);
    return (react_1.default.createElement(react_1.default.Fragment, null, !!searchResultPosition && (react_1.default.createElement(FabricMapMarkers_1.FabricMapMarker, { coordinates: searchResultPosition, className: "fabric-map-marker search-result" }))));
});
exports.FabricMapLocationSearch.displayName = "FabricMapLocationSearch";
